<template>
    <div class="header__breadcrumbs-wrapper">
        <ul v-if="!isSettings" ref="breadcrumbs" class="header__breadcrumbs">
            <li v-if="isComponentEditor && selectedComponentMetadata" class="header__breadcrumbs-item">
                <div class="header__breadcrumbs-button">
                    <span class="header__breadcrumbs-text">{{ componentType }} : {{ componentName }}</span>
                </div>
            </li>

            <li
                v-for="(page, index) in pages"
                :key="index"
                class="header__breadcrumbs-item"
            >
                <button
                    v-if="!templateMode && !isComponentEditor"
                    class="header__breadcrumbs-button"
                    type="button"
                    @click.stop="showHeaderPopover(index, $event)"
                >
                    <span class="header__breadcrumbs-text">{{ page | ellipses(30) }}</span>
                    <font-awesome-icon class="header__breadcrumbs-arrow" :icon="['fal', 'chevron-down']" />
                </button>

                <button v-else class="header__breadcrumbs-button" type="button">
                    <span class="header__breadcrumbs-text">{{ page | ellipses(30) }}</span>
                </button>
            </li>
        </ul>

        <breadcrumb-popover
            :data="selectedItemData"
            :popover-x="popoverX"
            :popover-y="popoverY"
            :show="isPopoverVisible"
            @close-popover="isPopoverVisible = false"
        />
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import BreadcrumbPopover from '@/components/page-editor/shared/header-breadcrumb/breadcrumb-popover';
import * as service from '@/sitecore/sitecore-data-service';
import { SHOW_ALERT } from '@/store/types/mutation-types';

export default {
    name: 'header-breadcrumb',

    components: {
        BreadcrumbPopover,
    },

    data() {
        return {
            isLoadingItemData: false,
            isPopoverVisible: false,
            popoverX: 0, // popover x-position
            popoverY: 0, // popover y-position
            selectedItemData: {},
            pathPrefix: '/sitecore/content',
            templatePrefix: '/sitecore/templates/project/website/page templates/',
        };
    },

    computed: {
        ...mapState({
            pageData: state => state.page.pageData,
            selectedComponentMetadata: state => state.page.selectedComponentMetadata,
        }),

        itemPath() {
            if (!this.pageData || !this.pageData.pageData.itemPath) return '';

            return this.pageData.pageData.itemPath;
        },

        pages() {
            // null checks
            if (!this.itemPath || this.isComponentEditor) return [];

            const path = this.itemPath.replace(this.pathPrefix, '');
            if (this.templateMode) {
                return this.itemPath.toLowerCase().replace(this.templatePrefix, '').split('/');
            }

            return path.split('/').filter(Boolean);
        },

        pathPrefixIndexLength() {
            return this.pathPrefix.split('/').length;
        },

        templateMode() {
            return this.$route.name === 'templates';
        },

        isComponentEditor() {
            return this.$route.name === 'components';
        },

        isSettings() {
            return this.$route.name === 'settings';
        },

        componentType() {
            return this.selectedComponentMetadata.componentType || 'Standard Component';
        },

        componentName() {
            return this.selectedComponentMetadata.editorName || '';
        },
    },

    watch: {
        pages() {
            this.scrollBreadcrumbs();
        },
    },

    methods: {
        ...mapMutations({
            showAlert: SHOW_ALERT,
        }),

        getPathByIndex(index) {
            // todo: check to see if we're in page editor
            index += this.pathPrefixIndexLength;

            return this.itemPath.split('/').slice(0, index + 1).join('/');
        },

        showHeaderPopover(index, event) {
            this.isPopoverVisible = false;
            // set position of popover:
            const container = event.currentTarget.getBoundingClientRect();

            this.popoverX = container.left + 13; // offset the padding (by 13px)
            this.popoverY = container.bottom;

            const path = this.getPathByIndex(index);
            this.getChildren(path);
        },

        async getChildren(path) {
            this.isLoadingItemData = true;
            const response = await service.getChildren(path);
            if (response.status === 'error') {
                this.showAlert({ description: response.error });
                return;
            }

            this.isLoadingItemData = false;
            this.selectedItemData = response.data;
            this.isPopoverVisible = true;
        },

        async scrollBreadcrumbs() {
            const hasSmoothScrollSupport = 'scrollBehavior' in document.documentElement.style;
            const { breadcrumbs } = this.$refs;
            if (breadcrumbs) {
                await this.$nextTick();

                if (hasSmoothScrollSupport) {
                    breadcrumbs.scrollTo({
                        top: 0,
                        left: breadcrumbs.clientWidth,
                        behavior: 'smooth',
                    });
                } else {
                    breadcrumbs.scrollLeft = breadcrumbs.clientWidth;
                }
            }
        },
    },
};
</script>

<style lang="scss">
@import "@/styles/includes/_variables.scss";
.header {
    $this: &;

    &__breadcrumbs-wrapper {
        position: static;
    }

    &__breadcrumbs {
        margin-left: 37px;
        padding: 0;
        display: flex;
        max-width: 42rem;
        overflow: hidden;
    }

    &__breadcrumbs-item {
        list-style: none;
    }

    &__breadcrumbs-button {
        display: flex;
        flex-direction: row;
        align-items: center;
        border: none;
        background-color: transparent;
        padding: 10px 15px;
    }

    &__breadcrumbs-text {
        font-size: 13px;
        white-space: nowrap;
    }

    &__breadcrumbs-arrow {
        margin-left: 10px;
        font-size: 11px;

        #{$this}__breadcrumbs-icon + & {
            position: relative;
            top: 1px;
        }
    }
}
</style>
